// A-LivePrice — Fetches live XAU/USD price. Zero AI. Yahoo Finance only.

import { ToolManager } from '../../brain/ToolManager';
import { MemoryManager } from '../../brain/MemoryManager';
import { NS } from '../../brain/AgentRegistry';

export async function run() {
  try {
    const result = await ToolManager.getGoldPrice();

    const priceData = {
      price:     result.price,
      change:    result.change,
      changePct: result.changePct,
      high:      result.high,
      low:       result.low,
      open:      result.open,
      volume:    result.volume,
      symbol:    'XAU/USD',
      source:    result.source,
      direction: result.change >= 0 ? 'UP' : 'DOWN',
      // Quick bias: if price above open → bullish, below → bearish
      bias:      result.price >= result.open ? 'BULLISH' : 'BEARISH',
      fromCache: result.fromCache,
      // stale/dataAgeMinutes/asOf — see ToolManager.getGoldPrice()'s
      // staleness check (added 2026-08-23). `stale` means the SOURCE
      // (Yahoo) itself reported this price more than 20 min ago — not to
      // be confused with `fromCache` (our own 30s local cache) or the
      // catch-block `stale` below (total fetch failure, using memory).
      stale:          result.stale ?? false,
      dataAgeMinutes: result.dataAgeMinutes ?? null,
      asOf:           result.asOf ?? null,
      ts: Date.now(),
    };

    // Save last known price to memory (survives page reload)
    MemoryManager.write(NS.TRADING, 'last_gold_price', priceData);

    return { ok: true, ...priceData, agent: 'A-LivePrice', requiresAI: false };

  } catch (err) {
    // Fallback: return last known price from memory
    const cached = MemoryManager.read(NS.TRADING, 'last_gold_price', null);
    if (cached) {
      return { ok: true, ...cached, stale: true, agent: 'A-LivePrice', requiresAI: false };
    }
    return { ok: false, error: err.message, agent: 'A-LivePrice', requiresAI: false, ts: Date.now() };
  }
}

// Combine `factor` consecutive candles into one larger candle — used for
// timeframes Yahoo Finance doesn't offer natively (45m, 4h). 45m = 3x15m,
// 4h = 4x1h — both are clean multiples so this is exact, not approximate.
function resampleCandles(candles, factor) {
  const out = [];
  for (let i = 0; i + factor <= candles.length; i += factor) {
    const group = candles.slice(i, i + factor);
    out.push({
      date:   group[0].date,
      open:   group[0].open,
      high:   Math.max(...group.map(c => c.high)),
      low:    Math.min(...group.map(c => c.low)),
      close:  group[group.length - 1].close,
      volume: group.reduce((s, c) => s + (c.volume || 0), 0),
    });
  }
  return out;
}

// Native Yahoo Finance intervals vs the two VALIDATED timeframes (45m, 4h)
// that need resampling from a native base — see jarvisDNA validated_strategy.
// ⚠️ 1m/3m/5m native/resampled ahet, pan Pine var mixed/weak results dile
// (3m: -0.35R, 5m: +0.06R @ ADX20 — 1m tar test kelach nahi). Ha module
// tyanna FETCH karto (data layer, block karat nahi) — pan LiveMonitor.jsx
// madhla isTimeframeApprovedForLive() gate live signal thambavto jopर्यंत
// tya specific TF cha saved backtest expectancy positive dakhat nahi.
const NATIVE_TF    = { '1m': '1m', '5m': '5m', '15m': '15m', '30m': '30m', '1h': '60m', '1d': '1d' };
const RESAMPLE_TF  = { '3m': { base: '1m', factor: 3 }, '45m': { base: '15m', factor: 3 }, '4h': { base: '60m', factor: 4 } };

// Fetch candles for one of the VALIDATED timeframes (15m/30m/45m/1h/4h/1d).
// `period` = how far back (Yahoo period string, e.g. '5d', '1mo', '3mo').
export async function getCandles(timeframe = '1h', period = '5d') {
  try {
    if (NATIVE_TF[timeframe]) {
      const { candles } = await ToolManager.getGoldCandles(period, NATIVE_TF[timeframe]);
      return { ok: true, candles, count: candles.length, timeframe, agent: 'A-LivePrice', ts: Date.now() };
    }
    if (RESAMPLE_TF[timeframe]) {
      const { base, factor } = RESAMPLE_TF[timeframe];
      const { candles: baseCandles } = await ToolManager.getGoldCandles(period, base);
      const candles = resampleCandles(baseCandles, factor);
      return { ok: true, candles, count: candles.length, timeframe, resampledFrom: base, agent: 'A-LivePrice', ts: Date.now() };
    }
    return { ok: false, error: `Unsupported timeframe "${timeframe}" — use 15m/30m/45m/1h/4h/1d`, candles: [], agent: 'A-LivePrice', ts: Date.now() };
  } catch (err) {
    return { ok: false, error: err.message, candles: [], agent: 'A-LivePrice', ts: Date.now() };
  }
}
